import { Edit2, Plus, Star, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useToast } from '../../context/ToastContext';
import collaborationService from '../../services/collaborationService';
import LoadingSpinner from '../ui/LoadingSpinner';
import Modal from '../ui/Modal';

const VARIABLES = ['{{influencer_name}}', '{{brand_name}}', '{{follower_count}}', '{{city}}', '{{niche}}'];

const COUNTRY_OPTIONS = ['India', 'USA', 'UK', 'UAE', 'Singapore', 'Australia'];

const defaultBody = `Hi {{influencer_name}},\n\nWe love your content and think you'd be a great fit for KAAY — a luxury fashion brand celebrating modern elegance.\n\nWe'd love to explore a collaboration! Would you be open to a quick chat about what we have in mind?\n\nLooking forward to hearing from you.\n\nWarm regards,\nTeam KAAY`;

function TemplateFormModal({ template, onClose, onSave }) {
  const { showToast } = useToast();
  const isEdit = !!template;
  const [form, setForm] = useState({
    name: template?.name || '',
    body: template?.body || defaultBody,
    is_default: template?.is_default || false,
    countries: template?.countries || [],
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.name.trim() || !form.body.trim()) {
      showToast('Name and body are required', 'error');
      return;
    }
    setSaving(true);
    try {
      let saved;
      if (isEdit) {
        saved = await collaborationService.updateTemplate(template.id, form);
      } else {
        saved = await collaborationService.createTemplate(form);
      }
      showToast(isEdit ? 'Template updated' : 'Template created', 'success');
      onSave(saved);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const insertVariable = (v) => {
    setForm((f) => ({ ...f, body: f.body + v }));
  };

  return (
    <Modal maxWidth="2xl" closeOnBackdrop={false} onClose={onClose} className="rounded-xl shadow-2xl flex flex-col">
      <div className="flex justify-between items-center px-6 py-4 border-b flex-shrink-0">
        <h2 className="text-lg font-semibold">{isEdit ? 'Edit Template' : 'New Template'}</h2>
        <button onClick={onClose} className="text-muted-foreground/70 hover:text-foreground">
          <X size={20} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground/80 mb-1">Template Name</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Cold Outreach v1"
            className="w-full px-3 py-2 text-sm border border-input rounded-lg focus:ring-2 focus:ring-foreground"
          />
        </div>

        <div>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
            <label className="block text-sm font-medium text-foreground/80">Message Body</label>
            <div className="flex gap-1">
              {VARIABLES.map((v) => (
                <button
                  key={v}
                  onClick={() => insertVariable(v)}
                  className="px-2 py-0.5 text-xs bg-muted text-muted-foreground rounded-md font-mono hover:bg-accent"
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
          <textarea
            rows={10}
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            className="w-full px-3 py-2 text-sm border border-input rounded-lg focus:ring-2 focus:ring-foreground resize-none font-mono"
          />
          <p className="text-xs text-muted-foreground/70 mt-1">
            Click a variable above to insert it at cursor position.
          </p>
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.is_default}
            onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
            className="rounded-lg border-input"
          />
          <span className="text-sm text-foreground/80">Set as default template</span>
        </label>

        <div>
          <label className="block text-sm font-medium text-foreground/80 mb-2">
            Target Countries
            <span className="ml-1 text-xs text-muted-foreground/70 font-normal">
              (leave empty for global / all countries)
            </span>
          </label>
          <div className="flex flex-wrap gap-2">
            {COUNTRY_OPTIONS.map((country) => {
              const selected = form.countries.includes(country);
              return (
                <button
                  key={country}
                  type="button"
                  onClick={() => {
                    const next = selected ? form.countries.filter((c) => c !== country) : [...form.countries, country];
                    setForm({ ...form, countries: next });
                  }}
                  className={`px-3 py-1 text-xs rounded-full border transition ${
                    selected
                      ? 'bg-primary text-primary-foreground border-foreground'
                      : 'bg-card text-muted-foreground border-input hover:border-gray-500'
                  }`}
                >
                  {country}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="px-6 pb-5 border-t pt-4 flex gap-3 justify-end flex-shrink-0">
        <button onClick={onClose} className="px-4 py-2 text-sm border border-input rounded-lg hover:bg-muted/50">
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
        >
          {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Template'}
        </button>
      </div>
    </Modal>
  );
}

export default function TemplatesTab() {
  const { showToast } = useToast();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await collaborationService.getTemplates();
      setTemplates(data);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = () => {
    setShowModal(false);
    setEditingTemplate(null);
    load();
  };

  const handleSetDefault = async (id) => {
    try {
      await collaborationService.setDefaultTemplate(id);
      showToast('Default template updated', 'success');
      load();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await collaborationService.deleteTemplate(id);
      showToast('Template deleted', 'success');
      load();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Outreach Templates</h2>
          <p className="text-muted-foreground text-sm mt-1">
            Manage message templates for influencer outreach. Variables: {'{{influencer_name}}'}, {'{{brand_name}}'},{' '}
            {'{{follower_count}}'}, {'{{city}}'}, {'{{niche}}'}.
          </p>
        </div>
        <button
          onClick={() => {
            setEditingTemplate(null);
            setShowModal(true);
          }}
          className="flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 rounded-lg hover:bg-primary/90 text-sm"
        >
          <Plus size={16} /> New Template
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <LoadingSpinner />
        </div>
      ) : templates.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-12 text-center">
          <p className="text-muted-foreground/70 text-lg mb-4">No templates yet</p>
          <button
            onClick={() => setShowModal(true)}
            className="bg-primary text-primary-foreground px-5 py-2 rounded-lg hover:bg-primary/90 text-sm"
          >
            Create your first template
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {templates.map((t) => (
            <div key={t.id} className="bg-card rounded-lg shadow p-5">
              <div className="flex justify-between items-start gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center flex-wrap gap-2 mb-1">
                    <h3 className="font-semibold text-foreground">{t.name}</h3>
                    {t.is_default && (
                      <span className="flex items-center gap-1 px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300 text-xs rounded-full font-medium">
                        <Star size={10} /> Default
                      </span>
                    )}
                    {!t.is_active && (
                      <span className="px-2 py-0.5 bg-muted text-muted-foreground text-xs rounded-full">Inactive</span>
                    )}
                    {t.countries?.length > 0 ? (
                      t.countries.map((c) => (
                        <span
                          key={c}
                          className="px-2 py-0.5 bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 text-xs rounded-full"
                        >
                          {c}
                        </span>
                      ))
                    ) : (
                      <span className="px-2 py-0.5 bg-muted/50 text-muted-foreground/70 text-xs rounded-full">
                        Global
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap line-clamp-3">{t.body}</p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  {!t.is_default && (
                    <button
                      onClick={() => handleSetDefault(t.id)}
                      title="Set as default"
                      className="p-1.5 text-muted-foreground/70 hover:text-yellow-500 transition"
                    >
                      <Star size={16} />
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setEditingTemplate(t);
                      setShowModal(true);
                    }}
                    className="p-1.5 text-muted-foreground/70 hover:text-foreground transition"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(t.id)}
                    className="p-1.5 text-muted-foreground/70 hover:text-red-500 transition"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <TemplateFormModal
          template={editingTemplate}
          onClose={() => {
            setShowModal(false);
            setEditingTemplate(null);
          }}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
